import * as z from "zod";
import { industries } from "@/data/industries";

export const profileSchema = z.object({
  bio_en: z.string().min(1, "Biography (English) is required"),
  avatar_url: z
    .string()
    .url("A valid profile picture is required")
    .min(1, "A valid profile picture is required"),
  display_name_en: z.string().min(1, "Full Name is required"),
  display_name_ar: z.string().min(1, "Full Name is required"),
  achievements: z.string().optional(),
  research_interests: z.string().optional(),
  scholarships: z.array(z.string()).default([]),
});

export const memberSchema = z
  .object({
    linkedin_url: z.string().optional(),
    is_employed: z.boolean().default(false),
    industry: z.string().optional(),
    position: z.string().optional(),
    employer: z.string().optional(),
    tier: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.is_employed) {
      if (!data.industry || data.industry.trim() === "") {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["industry"],
          message: "Industry is required if employed",
        });
      }
      if (!data.position || data.position.trim() === "") {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["position"],
          message: "Position is required if employed",
        });
      }
      if (!data.employer || data.employer.trim() === "") {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["employer"],
          message: "Organization is required if employed",
        });
      }
    }
  });

export const degreeSchema = z
  .object({
    id: z.string().optional(),
    degreeLevel: z.enum(["HighSchool", "Bachelors", "Masters", "PhD"], {
      required_error: "Degree Level is required",
    }),
    institution: z.string().optional(),
    institution_other_name: z.string().optional(),
    major: z.string().optional(),
    graduationYear: z.coerce
      .number()
      .min(1950, "Please enter a valid graduation year")
      .max(2035, "Please enter a valid graduation year"),
    gpa: z.string().optional(),
    degree_title: z.string().min(1, "Degree Title is required"),
    isNew: z.boolean().default(false).optional(),
    isDeleted: z.boolean().default(false).optional(),
    is_valedictorian: z.boolean().default(false).optional(),
    errors: z.record(z.string()).optional(),
  })
  .superRefine((data, ctx) => {
    if (
      data.degreeLevel !== "HighSchool" &&
      (!data.major || data.major.trim() === "")
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["major"],
        message: "Major is required for university degrees",
      });
    }
    if (
      data.degreeLevel === "HighSchool" &&
      (!data.gpa || data.gpa.trim() === "")
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["gpa"],
        message: "GPA is required for Highschool",
      });
    }
    // Institution validation: must have either institution or institution_other_name
    if (
      (!data.institution || data.institution.trim() === "") &&
      (!data.institution_other_name ||
        data.institution_other_name.trim() === "")
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["institution"],
        message: "University or School is required",
      });
    }

    // If "other" is selected, ensure institution_other_name is provided
    if (
      data.institution === "other" &&
      (!data.institution_other_name ||
        data.institution_other_name.trim() === "")
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["institution_other_name"],
        message: "Institution name is required when 'Other' is selected",
      });
    }
  });

export const memberProfileFormSchema = z
  .object({
    profile: profileSchema,
    member: memberSchema,
    degrees: z.array(degreeSchema),
  })
  .superRefine((data, ctx) => {
    const degrees = data.degrees;
    const memberTier = data.member.tier;

    if (memberTier === "Group 2") {
      const hasBachelors = degrees.some(
        (degree) => degree.degreeLevel === "Bachelors"
      );
      if (!hasBachelors) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["degrees"],
          message: "Group 2 members must have at least one Bachelor's degree",
        });
      }
    } else if (memberTier === "Group 3") {
      const hasMasters = degrees.some(
        (degree) => degree.degreeLevel === "Masters"
      );
      if (!hasMasters) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["degrees"],
          message: "Group 3 members must have at least one Master's degree",
        });
      }
    } else if (memberTier === "Group 4") {
      const hasPhd = degrees.some((degree) => degree.degreeLevel === "PhD");
      if (!hasPhd) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["degrees"],
          message: "Group 4 members must have at least one PhD degree",
        });
      }
    }
  });

export const applicationFormSchema = z
  .object({
    first_name: z.string().min(1, "First Name is required"),
    last_name: z.string().min(1, "Last Name is required"),
    email: z.string().email("Invalid email address"),
    phone_number: z.string().min(1, "Phone number is required"),
    date_of_birth: z
      .date({
        required_error: "Date of Birth is required",
      })
      .nullable()
      .superRefine((date, ctx) => {
        if (!date) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Date of Birth is required",
          });
          return;
        }

        const today = new Date();
        const minimumDate = new Date(
          today.getFullYear() - 16,
          today.getMonth(),
          today.getDate()
        );
        if (date > minimumDate) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "You must be at least 16 years old to apply",
          });
        }
      }),
    gender: z.enum(["male", "female"]),
    tier: z.enum(["Group 1", "Group 2", "Group 3", "Group 4"]),
    is_employed: z.boolean().default(false),
    industry: z.string().optional(),
    position: z.string().optional(),
    employer: z.string().optional(),
    linkedin_url: z.string().optional(),
    nationality: z.string().min(1, "Nationality is required"),
    cpr_number: z.string().min(1, "CPR is required"),
    degrees: z.array(degreeSchema),
    uploadedDocuments: z
      .array(
        z.object({
          file_name: z.string(),
          file_type: z.string(),
          storage_path: z.string(),
        })
      )
      .optional()
      .default([]),
  })
  .superRefine((data, ctx) => {
    if (data.is_employed) {
      if (!data.industry || data.industry.trim() === "") {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["industry"],
          message: "Industry is required if employed",
        });
      }
      if (!data.position || data.position.trim() === "") {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["position"],
          message: "Position is required if employed",
        });
      }
      if (!data.employer || data.employer.trim() === "") {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["employer"],
          message: "Organization is required if employed",
        });
      }
    }

    if (data.tier === "Group 2") {
      const hasBachelors = data.degrees.some(
        (degree) => degree.degreeLevel === "Bachelors"
      );
      if (!hasBachelors) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["degrees"],
          message: "Group 2 members must have at least one Bachelor's degree",
        });
      }
    } else if (data.tier === "Group 3") {
      const hasMasters = data.degrees.some(
        (degree) => degree.degreeLevel === "Masters"
      );
      if (!hasMasters) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["degrees"],
          message: "Group 3 members must have at least one Master's degree",
        });
      }
    } else if (data.tier === "Group 4") {
      const hasPhd = data.degrees.some(
        (degree) => degree.degreeLevel === "PhD"
      );
      if (!hasPhd) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["degrees"],
          message: "Group 4 members must have at least one PhD degree",
        });
      }
    }

    // Document upload validation
    if (!data.uploadedDocuments || data.uploadedDocuments.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["uploadedDocuments"],
        message: "At least one certificate document is required",
      });
    }
  });

export type ProfileFormType = z.infer<typeof profileSchema>;
export type MemberFormType = z.infer<typeof memberSchema>;
export type DegreeFormType = z.infer<typeof degreeSchema>;
export type MemberProfileFormType = z.infer<typeof memberProfileFormSchema>;
export type ApplicationFormType = z.infer<typeof applicationFormSchema>;
